import React, { useState, useEffect } from 'react'
import { View, Text, UIManager, LayoutAnimation } from 'react-native'
import PropTypes from 'prop-types'
import { VFButton, Icon, VFText } from '@vfgroup-oneplatform/foundation/Components'
import { Colors, useTheme, Images } from '@vfgroup-oneplatform/foundation/Components/Themes'

import VfText from '../VfText'
import PaymentOptions from '../PaymentOptions'
import VFLoader from '../VFLoader'
import ModalHeader from '../ModalHeader'

import { Images as images } from '../../Themes'

import { isArray } from '../../Utils'

import styles from './ConfirmationMenu.Styles'

UIManager.setLayoutAnimationEnabledExperimental && UIManager.setLayoutAnimationEnabledExperimental(true)

const CustomLayoutAnimation = {
  duration: 250,
  create: {
    type: LayoutAnimation.Types.easeInEaseOut,
    property: LayoutAnimation.Properties.opacity,
  },
  update: {
    type: LayoutAnimation.Types.easeInEaseOut,
  },
}

const PAYMENT_OPTIONS = {
  creditcard: {
    id: 'creditcard',
    icon: images.creditcard,
    name: 'catalogue_payment_type_creditcard',
  },
  bill: {
    id: 'bill',
    icon: images.bill,
    name: 'catalogue_payment_type_bill',
  },
  airtime: {
    id: 'airtime',
    icon: images.airtime,
    name: 'catalogue_payment_type_airtime',
  },
  loan: {
    id: 'loan',
    icon: images.loan,
    name: 'catalogue_payment_type_loan',
  },
}

function PaymentSelect({ paymentOptions, setPaymentMethod, closeModal, openPayments, setStep }) {

  const onOptionPress = ({ id }) => {
    setPaymentMethod(id)
    switch (id) {
      case 'airtime':
        setStep(STEPS.paymentConfirm)
        break
      case 'bill':
        setStep(STEPS.paymentConfirm)
        break
      case 'loan':
        setStep(STEPS.paymentLoan)
        break
      case 'creditcard':
        closeModal(openPayments)
        break
      default:
        break
    }
  }

  const theme = useTheme()

  return (
    <>
      <ModalHeader title="catalogue_payment_step_select" onClose={closeModal} />
      <VfText
        style={styles.mainText}
        i18nKey="catalogue_payment_label_payment_methods"
        weight="light"
        color={theme.colors.textColor}
      />
      <PaymentOptions
        options={paymentOptions}
        onOptionPress={onOptionPress}
      />
    </>
  )
}
PaymentSelect.propTypes = {
  paymentOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      icon: PropTypes.any,
    })
  ),
  setPaymentMethod: PropTypes.func,
  closeModal: PropTypes.func,
  openPayments: PropTypes.func,
  setStep: PropTypes.func,
}

function PaymentLoad({ closeModal }) {
  return (
    <>
      <ModalHeader title="catalogue_payment_step_loading" onClose={closeModal} />
      <VFLoader style={styles.loader} containerStyle={styles.loaderContainer} />
    </>
  )
}
PaymentLoad.propTypes = {
  closeModal: PropTypes.func,
}

function PaymentConfirm({
  paymentMethod,
  price = '',
  unit = '',
  closeModal,
  handleConfirmation,
}) {

  const theme = useTheme()

  return (
    <>
      <ModalHeader title="catalogue_payment_step_confirm" onClose={closeModal} />
      <VfText
        style={styles.mainText}
        i18nKey="catalogue_payment_desc_activation_confirm"
        weight="light"
        color={theme.colors.textColor}
      />
      <Text style={styles.priceContainer}>
        <VfText
          i18nKey="catalogue_payment_label_cost"
          size="footnote"
          color={theme.colors.textColor}
        />
        <VfText
          i18nKey="catalogue_payment_price_unit"
          placeHolders={[price, unit]}
          weight="bold"
          color={theme.colors.textColor}
        />
      </Text>
      {paymentMethod === 'bill' && (
        <VfText
          i18nKey="catalogue_payment_desc_next_bill"
          style={styles.note}
          color={theme.colors.textColor}
        />
      )}
      <VFButton
        style={styles.primaryButton}
        title="catalogue_payment_action_confirm"
        onPress={() => handleConfirmation()}
      />
      <VFButton
        style={styles.secondaryButton}
        title="catalogue_payment_action_back"
        type="secondary"
        onPress={closeModal}
      />
    </>
  )
}
PaymentConfirm.propTypes = {
  paymentMethod: PropTypes.string,
  price: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  unit: PropTypes.string,
  closeModal: PropTypes.func,
  handleConfirmation: PropTypes.func,
}

function PaymentLoan({
  prices: {
    loan: {
      price = '',
      unit = '',
      taxUnit = '',
      taxRate = '',
    } = {},
  } = {},
  closeModal,
  handleConfirmation,
}) {

  const theme = useTheme()

  return (
    <>
      <ModalHeader title="catalogue_payment_step_loan" onClose={closeModal} />
      <VfText
        i18nKey="catalogue_payment_title_loan_confirm"
        placeHolders={[price, unit]}
        style={styles.mainText}
        color={theme.colors.textColor}
      />
      <VfText
        i18nKey="catalogue_payment_desc_loan_confirm"
        placeHolders={[taxRate, taxUnit]}
        style={styles.note}
        color={theme.colors.textColor}
      />
      <VFButton
        style={styles.primaryButton}
        title="catalogue_payment_action_confirm"
        onPress={() => handleConfirmation()}
      />
      <VFButton
        style={styles.secondaryButton}
        title="catalogue_payment_action_back"
        type="secondary"
        onPress={closeModal}
      />
    </>
  )
}
PaymentLoan.propTypes = {
  prices: PropTypes.objectOf(PropTypes.object),
  closeModal: PropTypes.func,
  handleConfirmation: PropTypes.func,
}

function PaymentOverwrite({
  name: bundleName,
  closeModal,
  handleConfirmation,
  productDeleteItems,
}) {

  const theme = useTheme()

  const renderItem = ({ id, name } = {}, index) => (
    <View key={index + id} style={styles.productDeleteItem}>
      <Icon
        name={Images.remove}
        type="image"
        color={Colors.vfRed}
        resizeMode="contain"
        size={24}
      />
      <VfText
        i18nKey={name}
        size="h5"
        weight="bold"
        style={styles.productDeleteName}
        color={theme.colors.textColor}
      />
    </View>
  )

  return (
    <>
      <ModalHeader title="catalogue_payment_step_overwrite" onClose={closeModal} />
      <VfText
        style={styles.mainText}
        i18nKey="catalogue_payment_desc_overwrite"
        placeHolders={[bundleName]}
        weight="light"
        color={theme.colors.textColor}
      />
      <View style={styles.productDeleteItems}>
        {isArray(productDeleteItems) && productDeleteItems.map(renderItem)}
      </View>
      <VFButton
        style={styles.primaryButton}
        title="catalogue_payment_action_overwrite"
        onPress={() => handleConfirmation()}
      />
      <VFButton
        style={styles.secondaryButton}
        title="catalogue_payment_action_cancel"
        type="secondary"
        onPress={closeModal}
      />
    </>
  )
}
PaymentOverwrite.defaultProps = {
  productDeleteItems: [],
}
PaymentOverwrite.propTypes = {
  name: PropTypes.string,
  closeModal: PropTypes.func,
  handleConfirmation: PropTypes.func,
  productDeleteItems: PropTypes.array,
}

function PaymentSuccess({ closeModal, onBack }) {

  const theme = useTheme()
  return (
    <>
      <ModalHeader title="catalogue_payment_step_success" onClose={closeModal} />
      <Icon
        name={images.tick}
        type="image"
        size={120}
        resizeMode="contain"
        style={styles.success}
      />
      <VfText
        i18nKey="catalogue_payment_label_congrats"
        size="h3"
        weight="bold"
        align="center"
        style={styles.successTitle}
        color={theme.colors.textColor}
      />
      <VfText
        i18nKey="catalogue_payment_desc_bundle_activated"
        align="center"
        style={styles.successDesc}
        color={theme.colors.textColor}
      />
      <VFButton
        style={styles.successButton}
        title="catalogue_payment_action_confirm"
        onPress={() => { closeModal(onBack) }}
      />
    </>
  )
}
PaymentSuccess.propTypes = {
  closeModal: PropTypes.func,
  onBack: PropTypes.func,
}

function PaymentError({
  error,
  closeModal,
  handleConfirmation,
}) {

  const theme = useTheme()

  return (
    <>
      <Icon name={Images.ic_WarningHiLight_Theme} type="image" size={54} resizeMode="contain" style={styles.icon} />
      <VfText
        i18nKey="catalogue_label_something_went_wrong"
        size="h3"
        weight="bold"
        align="center"
        style={styles.title}
        color={theme.colors.textColor}
      />
      <VfText i18nKey={error} align="center" style={styles.description} color={theme.colors.textColor} />
      <VFButton
        title={'catalogue_action_retry'}
        onPress={() => handleConfirmation()}
        style={styles.primaryButton}
      />
      <VFButton
        style={styles.secondaryButton}
        title="catalogue_action_go_back"
        onPress={closeModal}
        type="secondary"
      />
    </>
  )
}
PaymentError.defaultProps = {
  error: '',
}
PaymentError.propTypes = {
  error: PropTypes.string,
  closeModal: PropTypes.func,
  handleConfirmation: PropTypes.func,
}

const STEPS = {
  paymentSelect: 'paymentSelect',
  paymentConfirm: 'paymentConfirm',
  paymentLoan: 'paymentLoan',
  paymentLoad: 'paymentLoad',
  paymentSuccess: 'paymentSuccess',
  paymentError: 'paymentError',
  paymentOverwrite: 'paymentOverwrite',
}

const PAYMENT_STEPS = {
  paymentSelect: PaymentSelect,
  paymentConfirm: PaymentConfirm,
  paymentLoan: PaymentLoan,
  paymentLoad: PaymentLoad,
  paymentSuccess: PaymentSuccess,
  paymentError: PaymentError,
  paymentOverwrite: PaymentOverwrite,
}

function ConfirmationMenu({ paymentMethods, loading, pending, completed, activate, closeModal, ...props }) {

  const { error } = props

  const [step, setStep] = useState(STEPS.paymentSelect)
  const [paymentMethod, setPaymentMethod] = useState()
  const [paymentOptions, setPaymentOptions] = useState([])

  const changeStep = value => {
    LayoutAnimation.configureNext(CustomLayoutAnimation)
    setStep(value)
  }

  const handleConfirmation = () => {
    activate(paymentMethod)
  }

  useEffect(() => {
    if (Array.isArray(paymentMethods)) {
      setPaymentOptions(() => {
        const options = []
        paymentMethods.forEach(type => {
          if (PAYMENT_OPTIONS[type]) {
            options.push(PAYMENT_OPTIONS[type])
          }
        })
        return options
      })
    }
  }, [paymentMethods])

  useEffect(() => {
    if (loading) {
      setStep(STEPS.paymentLoad)
    } else if (pending) {
      setStep(STEPS.paymentOverwrite)
    } else if (error) {
      setStep(STEPS.paymentError)
    } else if (completed) {
      setStep(STEPS.paymentSuccess)
    }
  }, [loading, pending, completed, error])

  const PaymentStep = PAYMENT_STEPS[step]
  return (
    <View>
      <PaymentStep
        paymentOptions={paymentOptions}
        setStep={changeStep}
        handleConfirmation={handleConfirmation}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        activate={activate}
        closeModal={closeModal}
        {...props}
      />
    </View>

  )
}
ConfirmationMenu.propTypes = {
  step: PropTypes.oneOf(Object.values(STEPS)),
  setStep: PropTypes.func,
  paymentMethods: PropTypes.array,
  loading: PropTypes.bool,
  pending: PropTypes.bool,
  completed: PropTypes.bool,
  error: PropTypes.string,
  activate: PropTypes.func,
}

export { STEPS }
export default ConfirmationMenu
